import axiosInstance from './AxiosInstance';

const BranchesService = {
  // 1. CREATE BRANCH
  createBranch: async (branchData) => {
    try {
      const response = await axiosInstance.post('/branches', branchData);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to create branch',
        errors: error.response?.data?.errors || []
      };
    }
  },

  // 2. GET ALL BRANCHES
  getAllBranches: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.search) queryParams.append('search', params.search);
      if (params.status) queryParams.append('status', params.status);
      if (params.city) queryParams.append('city', params.city);
      if (params.state) queryParams.append('state', params.state);
      if (params.country) queryParams.append('country', params.country);

      const response = await axiosInstance.get(`/branches?${queryParams.toString()}`);
      return {
        success: response.data.success,
        data: response.data.data.data,
        pagination: response.data.data.pagination,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch branches',
        data: [],
        pagination: {}
      };
    }
  },

  // 3. GET BRANCHES BY STATUS
  getBranchesByStatus: async (status) => {
    try {
      const response = await axiosInstance.get(`/branches/status/${status}`);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch branches by status',
        data: []
      };
    }
  },

  // 4. GET ACTIVE BRANCHES
  getActiveBranches: async () => {
    return await BranchesService.getBranchesByStatus('Active');
  },

  // 5. GET BRANCH BY ID
  getBranchById: async (id) => {
    try {
      const response = await axiosInstance.get(`/branches/${id}`);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch branch',
        data: null
      };
    }
  },

  // 6. UPDATE BRANCH
  updateBranch: async (id, branchData) => {
    try {
      const response = await axiosInstance.put(`/branches/${id}`, branchData);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update branch',
        errors: error.response?.data?.errors || []
      };
    }
  },

  // 7. SOFT DELETE BRANCH
  deleteBranch: async (id) => {
    try {
      const response = await axiosInstance.delete(`/branches/${id}`);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to delete branch'
      };
    }
  },

  // 8. GET BRANCH DROPDOWN
  getBranchDropdown: async () => {
    try {
      const response = await axiosInstance.get('/branches/dropdown');
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch branch dropdown',
        data: []
      };
    }
  },

  // 9. GET BRANCH DROPDOWN BY COMPANY
  getBranchDropdownByCompany: async (companyId) => {
    try {
      const response = await axiosInstance.get(`/branches/dropdown/company/${companyId}`);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch branch dropdown for company',
        data: []
      };
    }
  },

  // Legacy method names for backward compatibility
  getBranches: async (params = {}) => {
    return await BranchesService.getAllBranches(params);
  },

  getBranchesForSelect: async () => {
    return await BranchesService.getActiveBranches();
  },

  // Helper method for search
  searchBranches: async (searchTerm) => {
    return await BranchesService.getAllBranches({ search: searchTerm, limit: 50 });
  },

  // Helper method for filtering by location
  getBranchesByLocation: async (city, state, country) => {
    const params = {};
    if (city) params.city = city;
    if (state) params.state = state;
    if (country) params.country = country;
    
    return await BranchesService.getAllBranches(params);
  },

  // Helper method for getting branches by multiple statuses
  getBranchesByMultipleStatuses: async (statuses = []) => {
    try {
      const branchPromises = statuses.map(status => 
        BranchesService.getBranchesByStatus(status)
      );
      
      const results = await Promise.all(branchPromises);
      const allBranches = results.reduce((acc, result) => {
        if (result.success) {
          acc.push(...result.data);
        }
        return acc;
      }, []);

      return {
        success: true,
        data: allBranches,
        message: 'Branches fetched successfully'
      };
    } catch (error) {
      return {
        success: false,
        message: 'Failed to fetch branches by multiple statuses',
        data: []
      };
    }
  },

  // Get branches statistics
  getBranchesStatistics: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.companyId) queryParams.append('companyId', params.companyId);
      if (params.branchId) queryParams.append('branchId', params.branchId);

      const url = `/statistics/branches${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await axiosInstance.get(url);
      
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch branches statistics',
        data: {
          active_count: 0,
          inactive_count: 0,
          suspended_count: 0,
          total_count: 0
        }
      };
    }
  }
};

export default BranchesService;
